import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { isArrayLength } from '../../util/genericHelpers';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { upsertUserFavorites } from '../../util/favoritesPageHelpers';
import { parse } from '../../util/urlHelpers';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { retrieveUserFavoritesList } from '../../util/userDataExtraction';

export const FETCH_LISTING_REQUEST = 'app/FavoritesPage/FETCH_LISTING_REQUEST';
export const FETCH_LISTING_SUCCESS = 'app/FavoritesPage/FETCH_LISTING_SUCCESS';
export const FETCH_LISTING_ERROR = 'app/FavoritesPage/FETCH_LISTING_ERROR';

export const ADD_TO_FAVORITES = 'app/FavoritesPage/ADD_TO_FAVORITES';
export const ADD_TO_FAVORITES_REQUEST = 'app/FavoritesPage/ADD_TO_FAVORITES_REQUEST';
export const REMOVE_ITEM_FROM_FAVORITES_REQUEST =
  'app/FavoritesPage/REMOVE_ITEM_FROM_FAVORITES_REQUEST';
export const REMOVE_ITEM_FROM_FAVORITES_SUCCESS =
  'app/FavoritesPage/REMOVE_ITEM_FROM_FAVORITES_SUCCESS';
export const REMOVE_ITEM_FROM_FAVORITES_ERROR =
  'app/FavoritesPage/REMOVE_ITEM_FROM_FAVORITES_ERROR';
export const UPDATE_CURRENT_PAGE_RESULT_IDS = 'app/FavoritesPage/UPDATE_CURRENT_PAGE_RESULT_IDS';
export const CLEAN_FAVORITES_STATE = 'app/FavoritesPage/CLEAN_FAVORITES_STATE';

const RESULT_PAGE_SIZE = 42;

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  queryParams: null,
  favorites: [],
  addToFavoritesInProgress: false,
  favoritesCount: 0,
  fetchListingInProgress: false,
  fetchListingError: null,
  currentPageResultIds: [],
  removedItemId: null,
  removeItemFromFavoritesError: null,
};

const resultIds = data => data.data.map(l => l.id);

const favoritesPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case CLEAN_FAVORITES_STATE:
      return {
        ...state,
        addToFavoritesInProgress: false,
      };
    case ADD_TO_FAVORITES_REQUEST:
      return {
        ...state,
        addToFavoritesInProgress: true,
        currentFavoriteId: payload,
      };
    case ADD_TO_FAVORITES: {
      if (Array.isArray(payload)) {
        // Initialize state with an array of favorites IDs
        return {
          ...state,
          favorites: payload,
          favoritesCount: payload.length,
          currentFavoriteId: null,
        };
      }

      const { currentFavoriteId } = payload;
      const updatedFavorites = upsertUserFavorites({
        favorites: state.favorites,
        currentFavoriteId,
      });
      return {
        ...state,
        favorites: updatedFavorites,
        favoritesCount: updatedFavorites.length,
        currentFavoriteId: null,
      };
    }

    case FETCH_LISTING_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        fetchListingInProgress: true,
        currentPageResultIds: [],
        fetchListingError: null,
      };
    case FETCH_LISTING_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        fetchListingInProgress: false,
      };
    case UPDATE_CURRENT_PAGE_RESULT_IDS:
      return {
        ...state,
        currentPageResultIds: payload,
      };
    case FETCH_LISTING_ERROR:
      return { ...state, fetchListingInProgress: false, fetchListingError: payload };
    case REMOVE_ITEM_FROM_FAVORITES_REQUEST:
      return {
        ...state,
        removedItemId: payload,
        removeItemFromFavoritesError: null,
      };

    case REMOVE_ITEM_FROM_FAVORITES_SUCCESS:
      return {
        ...state,
        removedItemId: null,
        removeItemFromFavoritesError: null,
      };
    case REMOVE_ITEM_FROM_FAVORITES_ERROR:
      return {
        ...state,
        removedItemId: null,
        removeItemFromFavoritesError: payload,
      };
    default:
      return state;
  }
};

export default favoritesPageReducer;

// ================ Selectors ================ //
export const favoritesSelector = state => {
  const {
    addToFavoritesInProgress,
    favorites,
    favoritesCount,
    fetchListingInProgress,
    fetchListingError,
    currentPageResultIds,
    removedItemId,
    removeItemFromFavoritesError,
    currentFavoriteId,
  } = state.FavoritesPage;
  return {
    addToFavoritesInProgress,
    favorites,
    favoritesCount,
    fetchListingInProgress,
    fetchListingError,
    currentPageResultIds,
    removedItemId,
    removeItemFromFavoritesError,
    currentFavoriteId,
  };
};

// ================ Action creators ================ //

export const addToFavorites = data => ({ type: ADD_TO_FAVORITES, payload: data });
export const addToFavoritesRequest = id => ({ type: ADD_TO_FAVORITES_REQUEST, payload: id });
export const cleanFavoritesState = () => ({ type: CLEAN_FAVORITES_STATE });

export const fetchListingRequest = queryParams => ({
  type: FETCH_LISTING_REQUEST,
  payload: { queryParams },
});

export const fetchListingSuccess = response => ({
  type: FETCH_LISTING_SUCCESS,
  payload: { data: response.data },
});
export const updateCurrentPageResultIds = response => ({
  type: UPDATE_CURRENT_PAGE_RESULT_IDS,
  payload: response,
});

export const fetchListingError = e => ({
  type: FETCH_LISTING_ERROR,
  error: true,
  payload: e,
});
export const removeItemFromFavoritesRequest = id => ({
  type: REMOVE_ITEM_FROM_FAVORITES_REQUEST,
  payload: id,
});

export const removeItemFromFavoritesSuccess = () => ({
  type: REMOVE_ITEM_FROM_FAVORITES_SUCCESS,
});

export const removeItemFromFavoritesError = e => ({
  type: REMOVE_ITEM_FROM_FAVORITES_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //
export const addItemToFavorites = params => async (dispatch, getState, sdk) => {
  const { config, currentFavoriteId, favorites = [], isAuthenticated, userId } = params || {};
  dispatch(addToFavoritesRequest(currentFavoriteId));
  try {
    const updatedFavorites = upsertUserFavorites({
      favorites,
      currentFavoriteId,
    });

    dispatch(addToFavorites(updatedFavorites));
    if (isAuthenticated) {
      await dispatch(updateProfile({ publicData: { favorites: updatedFavorites } }));
    }
    return updatedFavorites;
  } catch (error) {
    console.error('Error adding item to favorites:', error);
    throw error;
  }
};

export const deleteItemFromFavorites = params => async (dispatch, getState, sdk) => {
  const { id, isAuthenticated, favorites = [], likes = [], userId, config } = params || {};
  dispatch(removeItemFromFavoritesRequest(id));
  try {
    const { currentPageResultIds } = getState().FavoritesPage;
    const updatedCurrentPageResultIds = currentPageResultIds.filter(item => item?.uuid !== id);
    let updatedFavorites = [...favorites]; // Create a copy to avoid mutation

    // Dispatch actions
    if (isAuthenticated && isArrayLength(favorites)) {
      // Remove id from the ids array of the appropriate category
      updatedFavorites = updatedFavorites
        .map(category => {
          if (category.ids?.includes(id)) {
            const updatedIds = category.ids.filter(itemId => itemId !== id);
            return { ...category, ids: updatedIds };
          }
          return category;
        })
        .filter(category => category.ids.length > 0); // Remove categories with empty ids

      await dispatch(updateProfile({ publicData: { favorites: updatedFavorites } }));
    }
    dispatch(addToFavorites(updatedFavorites));
    dispatch(removeItemFromFavoritesSuccess());
    dispatch(updateCurrentPageResultIds(updatedCurrentPageResultIds));
  } catch (error) {
    dispatch(removeItemFromFavoritesError(storableError(error)));
  }
};

export const fetchListings = (params, config) => async (dispatch, getState, sdk) => {
  dispatch(fetchListingRequest(params));
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;
  try {
    const queryParams = {
      ...params,
      include: ['author', 'images', 'currentStock'],
      'fields.listing': ['title', 'geolocation', 'price', 'description', 'publicData'],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
      'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    };

    const response = await sdk.listings.query(queryParams);
    dispatch(addMarketplaceEntities(response));
    dispatch(fetchListingSuccess(response));
    return response;
  } catch (error) {
    dispatch(fetchListingError(storableError(error)));
  }
};

export const loadData = (params, search, config) => async (dispatch, getState) => {
  try {
    const queryParams = parse(search);

    const page = queryParams.page || 1;
    const { currentUser } = (await getState().user) || {};
    const favorites = retrieveUserFavoritesList(currentUser) || [];
    const idsArray = isArrayLength(favorites) && favorites;
    if (isArrayLength(idsArray)) {
      dispatch(addToFavorites(favorites));

      dispatch(
        fetchListings(
          {
            ids: idsArray,
            page,
            perPage: RESULT_PAGE_SIZE,
          },
          config
        )
      );
    }
    return;
  } catch (error) {
    console.error('Error loading data:', error);
  }
};
